<!--
  Scheduled recrawls (FW-CRAWL-DELTA-01 phase 6). Dev view only — the component
  itself refuses to render or fetch outside it, so this is not the only gate.
-->
<section class="crawl-schedule card" *ngIf="isDevView()" [attr.aria-labelledby]="'crawl-schedule-heading-' + domain">
  <button type="button" class="crawl-schedule-toggle" (click)="toggle()" [attr.aria-expanded]="expanded">
    <span [attr.id]="'crawl-schedule-heading-' + domain" class="crawl-schedule-title">Scheduled recrawls</span>
    <span class="crawl-schedule-toggle-hint" *ngIf="schedule">{{ schedule.statusLine }}</span>
    <span class="crawl-schedule-chevron">{{ expanded ? '▾' : '▸' }}</span>
  </button>

  <div class="crawl-schedule-body" *ngIf="expanded">
    <p class="crawl-schedule-note" *ngIf="loading">Loading the schedule…</p>
    <p class="crawl-schedule-note crawl-schedule-note--error" *ngIf="error">{{ error }}</p>

    <ng-container *ngIf="!loading && schedule as current">
      <p class="crawl-schedule-status" [ngClass]="statusClass()">{{ current.statusLine }}</p>

      <dl class="crawl-schedule-facts">
        <div class="crawl-schedule-fact" *ngIf="current.nextCrawlAt">
          <dt>Next crawl</dt>
          <!-- Rendered in the viewer's timezone; the backend stores UTC. -->
          <dd>
            {{ current.nextCrawlAt | date: 'EEE d MMM, HH:mm' }}
            <span class="crawl-schedule-relative" *ngIf="current.nextCrawlLabel">({{ current.nextCrawlLabel }})</span>
          </dd>
        </div>
        <div class="crawl-schedule-fact" *ngIf="lastCrawlLine() as line">
          <dt>Last crawl</dt>
          <dd>
            {{ line }}
            <span class="crawl-schedule-relative" *ngIf="lastCrawlAt() as at">· {{ at | date: 'EEE d MMM, HH:mm' }}</span>
          </dd>
        </div>
        <div class="crawl-schedule-fact">
          <dt>Scheduled runs use</dt>
          <dd>{{ current.crawlOptions.crawlMode }} crawl, up to {{ current.crawlOptions.limit }} pages</dd>
        </div>
      </dl>

      <p class="crawl-schedule-note crawl-schedule-note--warn" *ngIf="current.crawlInProgress">
        A crawl is running for this domain right now. A due schedule waits for it to finish rather than
        starting a second one.
      </p>

      <p class="crawl-schedule-note crawl-schedule-note--warn" *ngIf="current.lastError">
        Last attempt failed: {{ current.lastError }} ({{ current.consecutiveFailures }} in a row)
      </p>

      <div class="crawl-schedule-controls">
        <label class="field">
          <span class="field__label">How often</span>
          <select class="input" [(ngModel)]="draftFrequency" [disabled]="saving">
            <option value="manual">Off — crawl by hand</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </label>

        <label class="field" *ngIf="draftFrequency !== 'manual'">
          <span class="field__label">Run next at</span>
          <input class="input" type="datetime-local" [(ngModel)]="draftNextCrawlLocal" [disabled]="saving" />
          <span class="field__hint">Your local time. Leave it alone to keep the current cadence.</span>
        </label>

        <!-- The one control here that can spend money, so it says what it costs
             in the label rather than in a tooltip. Checked = standing consent
             recorded against this domain; unchecked = the paid block stays
             behind its own Confirm in Setup, which is the default everywhere. -->
        <fieldset class="field crawl-schedule-enrichments">
          <legend class="field__label">Standing — run after every crawl (paid)</legend>
          <label class="crawl-schedule-enrichment" *ngFor="let step of enrichmentSteps">
            <input
              type="checkbox"
              [checked]="isEnrichmentChecked(step.id)"
              (change)="toggleEnrichment(step.id)"
              [disabled]="saving"
            />
            <span>{{ step.label }}</span>
          </label>
          <span class="field__hint">
            A standing authorization: it applies to scheduled runs, where nobody is there to confirm.
            Leave it off and nothing paid runs on its own.
          </span>
        </fieldset>

        <!-- Dev-only: the same shape a scheduled recrawl runs, on demand. Not
             shown to everyone yet because it spends without the priced confirm
             the Setup card puts in front of the same work. -->
        <div class="crawl-schedule-full" *ngIf="isDevView()">
          <!-- A separate box because it is a separate SCOPE: the fieldset above
               authorizes every future crawl, this runs one now. Rivals lives here
               and not up there because standing consent cannot authorize it --
               its cost scales with how many rivals you have, so a checkbox that
               pre-approved it would be approving an unknown number. -->
          <span class="field__label">This run only</span>
          <label class="crawl-schedule-enrichment">
            <input type="checkbox" [(ngModel)]="includeRivalsInFullCycle" [disabled]="startingFullCycle" />
            <span>Top SERP rivals — priced per run, ~$3.75 max</span>
          </label>
          <button
            type="button"
            class="btn btn--accent"
            (click)="startFullCycle()"
            [disabled]="startingFullCycle"
            title="Crawl this domain and every marked competitor, then run the ticked paid steps"
          >
            {{ startingFullCycle ? 'Starting…' : 'Start full crawl now (dev)' }}
          </button>
          <span class="field__hint">
            Runs now: focal domain + every marked competitor, the paid steps ticked above, and rivals
            if ticked here. The narrow <strong>Start crawl</strong> button higher up fetches this
            domain only.
          </span>
        </div>

        <div class="crawl-schedule-buttons">
          <button type="button" class="btn btn--accent" (click)="saveSchedule()" [disabled]="saving">
            {{ saving ? 'Saving…' : 'Save' }}
          </button>
          <button type="button" class="btn" (click)="pause()" *ngIf="canPause()" [disabled]="saving">
            Pause
          </button>
          <button type="button" class="btn" (click)="resume()" *ngIf="canResume()" [disabled]="saving">
            Resume
          </button>
          <button
            type="button"
            class="btn"
            (click)="postponeOneWeek()"
            *ngIf="current.nextCrawlAt"
            [disabled]="saving"
          >
            Postpone one week
          </button>
        </div>
      </div>

      <p class="crawl-schedule-footnote">
        A scheduled crawl runs through the same path as the Start crawl button above — same modes, same
        limits, same capture. Browser rendering stays off.
      </p>
    </ng-container>
  </div>
</section>
